import { buildErrorObj } from "@newsenec/logging";
import { markAsRead, ReadNotification } from "./fpp-adapter";
import { fetchNotifications as fetchFromNotificationService } from "./notification-service-adapter/adapter";
import mapNotification from "./notification-service-adapter/mapNotification";
import { mfShorty } from "./constants";
import { ErrorSerial } from "./ErrorSerial";

export interface CockpitNotification {
    /**
     * Unique identifier among all notifications.
     */
    id: string;

    /**
     * Message describing the notification.
     */
    message: string;

    /**
     * Distinguishes different types of notifications.
     *
     * All consumers and providers of notifications are responsible to agree about
     * types. Some (but not necessarily all) types agreed upon are listed as
     * [CockpitNotificationTypes].
     *
     * @see CockpitNotificationTypes
     */
    type: string;

    /**
     * Time at which the event happened that caused the notification.
     */
    time: Date;
}

export enum CockpitNotificationTypes {
    TechnicalDocument = "TechnicalDocument",
}

export interface Acknowledgement {
    acknowledged: CockpitNotification[];
    unacknowledged: CockpitNotification[];
}

/**
 * Acknowledges the given notifications.
 *
 * Currently, we do not acknowledge a specific notification.
 * Instead, the latest time of the given notifications is taken as a reference.
 * All notifications up to this point in time are acknowledged.
 *
 * Notice that this may acknowledge notifications that originate at an earlier
 * point in time and were not yet forwarded to the notification provider.
 */
export async function acknowledge(notifications: CockpitNotification[]): Promise<Acknowledgement> {
    if (notifications.length <= 0) {
        return Promise.resolve({ acknowledged: [], unacknowledged: [] });
    }
    const readNotifications: ReadNotification[] = await markAsRead(notifications).catch(() => []);
    return separateAcknowledged(notifications, readNotifications);
}

function separateAcknowledged(notifications: CockpitNotification[], readNotifications: ReadNotification[]) {
    return {
        acknowledged: notifications.filter((n) => readNotifications.some((read) => equals(read, n))),
        unacknowledged: notifications.filter((n) => !readNotifications.some((readN) => equals(readN, n))),
    };
}

/**
 * Returns the new notification, i.e., notifications that the current user did
 * not yet acknowledge.
 */
export async function fetchNotifications(): Promise<CockpitNotification[]> {
    try {
        const userNotifications = await fetchFromNotificationService();
        return userNotifications.map(mapNotification);
    } catch (error) {
        return Promise.reject(buildErrorObj(mfShorty, error, ErrorSerial.NOTIFICATIONS));
    }
}

function equals(readNotification: ReadNotification, notification: CockpitNotification) {
    return readNotification.type === notification.type && readNotification.id === notification.id;
}
